import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Download, Loader2, ImageIcon, CheckCircle } from 'lucide-react';
import { ChessAppLayout } from '@/components/ChessAppLayout';
import { supabase } from '@/integrations/supabase/client';

// Import all current piece images
import aiWhiteKing from '@/assets/pieces/ai-white-king.png';
import aiWhiteQueen from '@/assets/pieces/ai-white-queen.png';
import aiWhiteRook from '@/assets/pieces/ai-white-rook.png';
import aiWhiteBishop from '@/assets/pieces/ai-white-bishop.png';
import aiWhiteKnight from '@/assets/pieces/ai-white-knight.png';
import aiWhitePawn from '@/assets/pieces/ai-white-pawn.png';
import aiBlackKing from '@/assets/pieces/ai-black-king.png';
import aiBlackQueen from '@/assets/pieces/ai-black-queen.png';
import aiBlackRook from '@/assets/pieces/ai-black-rook.png';
import aiBlackBishop from '@/assets/pieces/ai-black-bishop.png';
import aiBlackKnight from '@/assets/pieces/ai-black-knight.png';
import aiBlackPawn from '@/assets/pieces/ai-black-pawn.png';

interface PieceData {
  name: string;
  filename: string;
  src: string;
  processedSrc?: string;
  status: 'pending' | 'processing' | 'done' | 'error';
}

const initialPieces: PieceData[] = [
  { name: 'White King', filename: 'ai-white-king.png', src: aiWhiteKing, status: 'pending' },
  { name: 'White Queen', filename: 'ai-white-queen.png', src: aiWhiteQueen, status: 'pending' },
  { name: 'White Rook', filename: 'ai-white-rook.png', src: aiWhiteRook, status: 'pending' },
  { name: 'White Bishop', filename: 'ai-white-bishop.png', src: aiWhiteBishop, status: 'pending' },
  { name: 'White Knight', filename: 'ai-white-knight.png', src: aiWhiteKnight, status: 'pending' },
  { name: 'White Pawn', filename: 'ai-white-pawn.png', src: aiWhitePawn, status: 'pending' },
  { name: 'Black King', filename: 'ai-black-king.png', src: aiBlackKing, status: 'pending' },
  { name: 'Black Queen', filename: 'ai-black-queen.png', src: aiBlackQueen, status: 'pending' },
  { name: 'Black Rook', filename: 'ai-black-rook.png', src: aiBlackRook, status: 'pending' },
  { name: 'Black Bishop', filename: 'ai-black-bishop.png', src: aiBlackBishop, status: 'pending' },
  { name: 'Black Knight', filename: 'ai-black-knight.png', src: aiBlackKnight, status: 'pending' },
  { name: 'Black Pawn', filename: 'ai-black-pawn.png', src: aiBlackPawn, status: 'pending' },
];

export default function RemoveBackgrounds() {
  const [pieces, setPieces] = useState<PieceData[]>(initialPieces);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);

  const imageToBase64 = async (imageSrc: string): Promise<string> => {
    const response = await fetch(imageSrc);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const processOnePiece = async (piece: PieceData): Promise<string | null> => {
    try {
      const base64Image = await imageToBase64(piece.src);
      
      const { data, error } = await supabase.functions.invoke('remove-background', {
        body: { imageUrl: base64Image, pieceName: piece.name }
      });

      if (error) throw error;
      if (!data?.processedImageUrl) throw new Error('No processed image returned');

      return data.processedImageUrl;
    } catch (err) {
      console.error(`Error processing ${piece.name}:`, err);
      return null;
    }
  };

  const processSinglePiece = async (index: number) => {
    const piece = pieces[index];
    
    setPieces(prev => prev.map((p, i) => 
      i === index ? { ...p, status: 'processing' } : p
    ));

    const processedSrc = await processOnePiece(piece);

    setPieces(prev => prev.map((p, i) => 
      i === index ? { 
        ...p, 
        status: processedSrc ? 'done' : 'error',
        processedSrc: processedSrc || undefined
      } : p
    ));

    if (processedSrc) {
      toast.success(`${piece.name} processed successfully!`);
    } else {
      toast.error(`Failed to process ${piece.name}`);
    }
  };

  const processAllPieces = async () => {
    setIsProcessing(true);
    setProgress(0);

    for (let i = 0; i < pieces.length; i++) {
      if (pieces[i].status !== 'done') {
        setPieces(prev => prev.map((p, idx) => 
          idx === i ? { ...p, status: 'processing' } : p
        ));

        const processedSrc = await processOnePiece(pieces[i]);

        setPieces(prev => prev.map((p, idx) => 
          idx === i ? { 
            ...p, 
            status: processedSrc ? 'done' : 'error',
            processedSrc: processedSrc || undefined
          } : p
        ));

        setProgress(((i + 1) / pieces.length) * 100);
        
        // Small delay to avoid rate limiting
        if (i < pieces.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
    }

    setIsProcessing(false);
    toast.success('All pieces processed!');
  };

  const downloadPiece = (piece: PieceData) => {
    if (!piece.processedSrc) return;
    
    const link = document.createElement('a');
    link.href = piece.processedSrc;
    link.download = piece.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAll = () => {
    const processed = pieces.filter(p => p.processedSrc);
    processed.forEach((piece, i) => {
      setTimeout(() => downloadPiece(piece), i * 200);
    });
  };

  const doneCount = pieces.filter(p => p.status === 'done').length;

  return (
    <ChessAppLayout>
      <div className="container mx-auto py-8 px-4">
        <div className="max-w-6xl mx-auto space-y-8">
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold">Remove Piece Backgrounds</h1>
            <p className="text-muted-foreground">
              Process your AI-generated chess pieces to have transparent backgrounds
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Processing Controls</span>
                <span className="text-sm font-normal text-muted-foreground">
                  {doneCount} / {pieces.length} completed
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <Button 
                  onClick={processAllPieces} 
                  disabled={isProcessing}
                  className="flex-1"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <ImageIcon className="w-4 h-4 mr-2" />
                      Remove All Backgrounds
                    </>
                  )}
                </Button>
                <Button 
                  onClick={downloadAll} 
                  disabled={doneCount === 0}
                  variant="outline"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download All ({doneCount})
                </Button>
              </div>
              {isProcessing && (
                <Progress value={progress} className="h-2" />
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {pieces.map((piece, index) => (
              <Card key={piece.filename} className="overflow-hidden">
                <CardContent className="p-4 space-y-3">
                  <div className="text-center font-medium text-sm">{piece.name}</div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    {/* Original */}
                    <div className="space-y-1">
                      <div className="text-xs text-muted-foreground text-center">Original</div>
                      <div className="aspect-square bg-muted rounded-lg flex items-center justify-center p-2">
                        <img 
                          src={piece.src} 
                          alt={`${piece.name} original`}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    </div>
                    
                    {/* Processed */}
                    <div className="space-y-1">
                      <div className="text-xs text-muted-foreground text-center">Processed</div>
                      <div 
                        className="aspect-square rounded-lg flex items-center justify-center p-2"
                        style={{ 
                          background: 'repeating-conic-gradient(#808080 0% 25%, #c0c0c0 0% 50%) 50% / 20px 20px'
                        }}
                      >
                        {piece.processedSrc ? (
                          <img 
                            src={piece.processedSrc} 
                            alt={`${piece.name} processed`}
                            className="max-w-full max-h-full object-contain"
                          />
                        ) : piece.status === 'processing' ? (
                          <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => processSinglePiece(index)}
                      disabled={isProcessing || piece.status === 'processing'}
                    >
                      {piece.status === 'processing' ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : piece.status === 'done' ? (
                        <CheckCircle className="w-3 h-3 text-green-500" />
                      ) : (
                        'Process'
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => downloadPiece(piece)}
                      disabled={!piece.processedSrc}
                    >
                      <Download className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="bg-muted/50">
            <CardContent className="p-4">
              <h3 className="font-semibold mb-2">Next Steps</h3>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                <li>Click "Remove All Backgrounds" to process each piece</li>
                <li>Review the processed images (checkerboard shows transparency)</li>
                <li>Download all processed pieces</li>
                <li>Upload them to replace the current pieces in the project</li>
              </ol>
            </CardContent>
          </Card>
        </div>
      </div>
    </ChessAppLayout>
  );
}
